import { ContractRoles, PERSTokenContractTypes } from "../contractMetaData.js";
import { PERS_CREDIT_TOKEN_ETHERS_METHOD_BUILDER, PERS_GENERIC_ETHERS_METHOD_BUILDER, PERS_REWARD_TOKEN_ETHERS_METHOD_BUILDER, PERS_STATUS_TOKEN_ETHERS_METHOD_BUILDER } from "./PERS_METHOD_BUILDER.ethers.functions.js";
import { stringToBytes32Hash } from "../../helpers/encoders.js";
import { getAddressTokenBalanceByContract } from "../../functions/contract.ethers.functions.js";
export const checkTokenContractType = async (tokenContract) => {
    try {
        // Try to call uri method to check if it's a reward token (ERC1155)
        const uri = await tokenContract.uri(0);
        return PERSTokenContractTypes.PERS_REWARD_TOKEN;
    }
    catch (error) {
        try {
            // Check if it supports ERC721 interface
            const supportsInterfaceMethod = PERS_STATUS_TOKEN_ETHERS_METHOD_BUILDER(tokenContract, 'supportsInterface');
            const isERC721 = await supportsInterfaceMethod('0x80ac58cd');
            return isERC721 ? PERSTokenContractTypes.PERS_STATUS_TOKEN : PERSTokenContractTypes.PERS_CREDIT_TOKEN;
        }
        catch (innerError) {
            return PERSTokenContractTypes.PERS_CREDIT_TOKEN;
        }
    }
};
export const checkIfAddressIsMinter = async (tokenContract, address) => {
    // TODO this will only work for contracts that have a minter role
    const role = ContractRoles.MINTER_ROLE;
    try {
        const method = PERS_GENERIC_ETHERS_METHOD_BUILDER(tokenContract, 'hasRole');
        const isMinter = await method(stringToBytes32Hash(role), address);
        return isMinter;
    }
    catch (error) {
        console.error('Error checking if address is minter:', error);
        throw error;
    }
};
export const getAccountTokenBalance = async (tokenContract, accountAddress, tokenId) => {
    try {
        const balance = await getAddressTokenBalanceByContract(tokenContract, accountAddress, tokenId);
        return balance;
    }
    catch (error) {
        console.error('Error getting account token balance:', error);
        throw error;
    }
};
export const getTokenUri = async (tokenContract, tokenId) => {
    const contractType = await checkTokenContractType(tokenContract);
    switch (contractType) {
        case PERSTokenContractTypes.PERS_CREDIT_TOKEN:
            throw new Error(`Contract does not support method 'uri'`);
        case PERSTokenContractTypes.PERS_REWARD_TOKEN:
            if (tokenId === null)
                throw new Error('TokenId is required');
            const uriMethod = PERS_REWARD_TOKEN_ETHERS_METHOD_BUILDER(tokenContract, 'uri');
            const uri = await uriMethod(tokenId);
            // if uri ends with {id}.json, replace it with the tokenId
            return uri.endsWith('{id}.json') ? uri.replace('{id}', tokenId.toString()) : uri;
        case PERSTokenContractTypes.PERS_STATUS_TOKEN:
            if (tokenId === null)
                throw new Error('TokenId is required');
            const tokenURIMethod = PERS_STATUS_TOKEN_ETHERS_METHOD_BUILDER(tokenContract, 'tokenURI');
            const tokenURI = await tokenURIMethod(tokenId);
            return tokenURI;
        default:
            throw new Error('Contract not supported');
    }
};
export const checkIfAddressIsApprovedForAll = async (tokenContract, ownerAddress, operatorAddress) => {
    const contractType = await checkTokenContractType(tokenContract);
    if (contractType !== PERSTokenContractTypes.PERS_REWARD_TOKEN) {
        throw new Error('Contract does not support method isApprovedForAll');
    }
    const method = PERS_REWARD_TOKEN_ETHERS_METHOD_BUILDER(tokenContract, 'isApprovedForAll');
    const isApproved = await method(ownerAddress, operatorAddress);
    return isApproved;
};
export const getContractName = async (tokenContract) => {
    const contractType = await checkTokenContractType(tokenContract);
    switch (contractType) {
        case PERSTokenContractTypes.PERS_CREDIT_TOKEN:
            const creditNameMethod = PERS_CREDIT_TOKEN_ETHERS_METHOD_BUILDER(tokenContract, 'name');
            return await creditNameMethod();
        case PERSTokenContractTypes.PERS_REWARD_TOKEN:
            throw new Error(`Contract does not support method 'name'; TODO ERC1155`);
        case PERSTokenContractTypes.PERS_STATUS_TOKEN:
            const statusNameMethod = PERS_STATUS_TOKEN_ETHERS_METHOD_BUILDER(tokenContract, 'name');
            return await statusNameMethod();
        default:
            throw new Error('Contract not supported');
    }
};
export const getContractSymbol = async (tokenContract) => {
    const contractType = await checkTokenContractType(tokenContract);
    switch (contractType) {
        case PERSTokenContractTypes.PERS_CREDIT_TOKEN:
            const creditSymbolMethod = PERS_CREDIT_TOKEN_ETHERS_METHOD_BUILDER(tokenContract, 'symbol');
            return await creditSymbolMethod();
        case PERSTokenContractTypes.PERS_REWARD_TOKEN:
            throw new Error(`Contract does not support method 'symbol'; TODO ERC1155`);
        case PERSTokenContractTypes.PERS_STATUS_TOKEN:
            const statusSymbolMethod = PERS_STATUS_TOKEN_ETHERS_METHOD_BUILDER(tokenContract, 'symbol');
            return await statusSymbolMethod();
        default:
            throw new Error('Contract not supported');
    }
};
export const getCurrentContractNonce = async (tokenContract, accountAddress) => {
    const contractType = await checkTokenContractType(tokenContract);
    switch (contractType) {
        case PERSTokenContractTypes.PERS_CREDIT_TOKEN:
            const noncesMethod = PERS_CREDIT_TOKEN_ETHERS_METHOD_BUILDER(tokenContract, 'nonces');
            return await noncesMethod(accountAddress);
        case PERSTokenContractTypes.PERS_REWARD_TOKEN:
            throw new Error('Nonces in Contract not supported, TODO ERC1155');
        case PERSTokenContractTypes.PERS_STATUS_TOKEN:
            throw new Error('Nonces in Contract not supported, TODO ERC721');
        default:
            throw new Error('Contract not supported');
    }
};
export const getTokenOfOwnerByIndex = async (tokenContract, ownerAddress, index) => {
    const contractType = await checkTokenContractType(tokenContract);
    switch (contractType) {
        case PERSTokenContractTypes.PERS_CREDIT_TOKEN:
            throw new Error('tokenOfOwnerByIndex not supported for Credit Token');
        case PERSTokenContractTypes.PERS_REWARD_TOKEN:
            throw new Error('tokenOfOwnerByIndex not supported for Reward Token');
        case PERSTokenContractTypes.PERS_STATUS_TOKEN:
            const tokenOfOwnerByIndexMethod = PERS_STATUS_TOKEN_ETHERS_METHOD_BUILDER(tokenContract, 'tokenOfOwnerByIndex');
            return await tokenOfOwnerByIndexMethod(ownerAddress, index);
        default:
            throw new Error('Contract not supported');
    }
};
